import { useEffect, useRef } from 'react'
import { useSetPan, useSetZoom } from '@sketch-hq/sketch-web-renderer'

import { useCanvasScope } from '../scope'

/**
 * Where a canvas was left. Structural rather than imported from the view that
 * stores it, so this module keeps depending on nothing above it.
 */
export interface CanvasCameraPosition {
  initialZoom: number | undefined
  initialPan:
    | { x: number; y: number; centerPointInViewport: boolean }
    | undefined
}

/**
 * Puts a canvas back where it was left whenever the scene root changes.
 *
 * Each scene root - the page, a Section, a frame - remembers a camera position
 * of its own, and the renderer reads the one it is given when it is built. That
 * used to be enough: the page, Section and frame views each mounted a canvas,
 * so every navigation between them rebuilt the renderer and it opened where you
 * had left that root. One canvas serving all three is never rebuilt, so nothing
 * read those positions any more and every move ended at a fit - of the frame
 * being opened, or of whatever a closing one uncovered.
 *
 * So the read happens here instead, on the move rather than at init, and for
 * every direction: opening an entity, closing it, and stepping sideways between
 * siblings through a selector. A root with nothing remembered - a Section opened
 * for the first time - is left to the renderer's fit, which is what should
 * happen and what the fit is for.
 *
 * Not on mount: the renderer still reads its initial position when it is built,
 * and that is also where a "Copy Link to Position" link lands.
 *
 * @param cameraPosition Where the canvas being moved to was left. Already
 * scoped to it, since the caller reads the position for whatever the scope
 * resolves to on the same render.
 */
export function useRestoreCanvasCamera(cameraPosition?: CanvasCameraPosition) {
  const scope = useCanvasScope()
  const setZoom = useSetZoom()
  const setPan = useSetPan()

  /** The canvas's scene root, undefined being the page itself. */
  const sceneRoot = scope?.entityUUID

  // Read when the root changes rather than depended on: it is recomputed from
  // local storage every render, and a new object must not re-run the effect
  // below - that would fight whatever the reader has done since.
  const cameraRef = useRef(cameraPosition)
  cameraRef.current = cameraPosition

  // Starts where it is, so the first run has nothing to move from.
  const previousRoot = useRef(sceneRoot)

  useEffect(() => {
    const hasMoved = previousRoot.current !== sceneRoot
    previousRoot.current = sceneRoot

    if (!hasMoved) {
      return
    }

    const { initialZoom, initialPan } = cameraRef.current ?? {}

    if (initialZoom !== undefined) setZoom(initialZoom)
    if (initialPan !== undefined) setPan(initialPan)
  }, [sceneRoot, setZoom, setPan])
}
